import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import { validatePhoneNumber } from '../../helpers/phone';

/**
 * Reusable PhoneNumberInput component with real-time validation
 * 
 * @param {string} value - Current phone number value
 * @param {function} onChange - Callback with (value, isValid, errorMessage)
 * @param {string} label - Label text (default: "Numero")
 * @param {string} placeholder - Placeholder text (default: "+19107422001")
 * @param {string} id - Input id (default: "phoneNumber")
 * @param {string} name - Input name (default: "phoneNumber")
 * @param {string} className - Additional CSS classes for wrapper
 * @param {boolean} required - Whether field is required (default: true)
 * @param {boolean} disabled - Whether input is disabled
 */
export default function PhoneNumberInput({
    value = '',
    onChange,
    label = 'Numero',
    placeholder = '+19107422001',
    id = 'phoneNumber',
    name = 'phoneNumber',
    className = '',
    required = true,
    disabled = false
}) {
    const [isValid, setIsValid] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [hasBlurred, setHasBlurred] = useState(false);

    // Validate on value change (for external value updates, e.g., from customer selection)
    useEffect(() => {
        const validation = validatePhoneNumber(value, required);
        setIsValid(validation.isValid);
        setErrorMessage(validation.errorMessage);
    }, [value, required]);

    const handleBlur = () => {
        setHasBlurred(true);
    };

    const handleChange = (e) => {
        const newValue = e.target.value;
        // Validate immediately and notify parent
        const validation = validatePhoneNumber(newValue, required);
        setIsValid(validation.isValid);
        setErrorMessage(validation.errorMessage);
        
        // Notify parent component
        if (onChange) {
            onChange(newValue, validation.isValid, validation.errorMessage);
        }
    };

    // Show error only after user has interacted with the field
    const showError = hasBlurred || (value && !isValid);

    return (
        <div className={`formEntry ${className}`}>
            <label htmlFor={id} className="form-label">{label}</label>
            <InputMask
                mask="+19999999999"
                maskChar={null}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
            >
                {(inputProps) => (
                    <input
                        {...inputProps}
                        type="text"
                        className={isValid ? 'validInputField' : 'invalidInputField'}
                        id={id}
                        name={name}
                        placeholder={placeholder}
                    />
                )}
            </InputMask>
            {showError && !isValid && errorMessage && (
                <div className="phone-error-message" style={{
                    color: '#dc3545',
                    fontSize: '0.875rem',
                    marginTop: '0.25rem'
                }}>
                    {errorMessage}
                </div>
            )}
        </div>
    );
}
